import { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

export function ForgotPassword() {
  const { forgotPassword, confirmForgotPassword, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Already authenticated — redirect to dashboard
  if (!authLoading && user) return <Navigate to="/" replace />;

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setStep('reset');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmForgotPassword(email, code, newPassword);
      navigate('/signin');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-900 p-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center justify-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 text-lg font-bold text-white">
            S
          </div>
          <h1 className="text-2xl font-bold tracking-tight">ShipdIt</h1>
        </div>

        <div className="rounded-xl border border-gray-800 bg-surface-850 p-6">
          {step === 'request' ? (
            <form onSubmit={handleRequest}>
              <h2 className="mb-2 text-center text-lg font-semibold">Forgot Password</h2>
              <p className="mb-6 text-center text-sm text-gray-400">Enter your email to receive a reset code</p>

              {error && (
                <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-400">
                  {error}
                </div>
              )}

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">Email</label>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-6 w-full rounded-lg bg-primary-600 py-2.5 text-sm font-semibold text-white hover:bg-primary-500 disabled:opacity-50"
              >
                {loading ? 'Sending…' : 'Send Reset Code'}
              </button>

              <p className="mt-4 text-center text-xs text-gray-500">
                <Link to="/signin" className="text-primary-400 hover:text-primary-300">
                  Back to sign in
                </Link>
              </p>
            </form>
          ) : (
            <form onSubmit={handleReset}>
              <h2 className="mb-2 text-center text-lg font-semibold">Reset Password</h2>
              <p className="mb-6 text-center text-sm text-gray-400">
                Enter the code sent to <span className="text-gray-200">{email}</span>
              </p>

              {error && (
                <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-400">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">Code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-center text-lg tracking-widest text-gray-100 outline-none focus:border-primary-500"
                    placeholder="000000"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                    New Password
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
                    placeholder="Min. 8 characters"
                    required
                    minLength={8}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-6 w-full rounded-lg bg-primary-600 py-2.5 text-sm font-semibold text-white hover:bg-primary-500 disabled:opacity-50"
              >
                {loading ? 'Resetting…' : 'Reset Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
